/**
 * Header context (TMMAPP-0004, TMMAPP-0072; the v2 top bar, TMMAPP-0150 U2). Pages publish what
 * the bar shows with `useHeader()`; the shell reads it with `useHeaderContent()`. One provider
 * above the routes (`HeaderContextProvider`, mounted by `AppShell`) carries both.
 *
 * The in-page `PageHeader` is the page's heading: the v2 top bar shows no title at any width
 * (§B 10). `title` still feeds `document.title` through `usePageTitle`. The bar carries no privacy
 * chip (TMMAPP-0161 item 3). The provider keeps a stack of entries (one per mounted `useHeader`
 * call, in mount order) and shows the newest, so one call that unmounts never blanks another that
 * is still mounted.
 */
import { createContext, useContext, useEffect, useId } from 'react';

export interface HeaderContent {
  /** The page's title (for the tab); null while it loads. */
  title: string | null;
}

export const EMPTY_HEADER: HeaderContent = { title: null };

export interface HeaderContextValue {
  content: HeaderContent;
  /** Adds the entry, or replaces it in place (keeping its position in the stack). */
  setEntry: (id: string, content: HeaderContent) => void;
  removeEntry: (id: string) => void;
}

export const HeaderContext = createContext<HeaderContextValue>({
  content: EMPTY_HEADER,
  setEntry: () => {},
  removeEntry: () => {},
});

/** Sets the header content for the lifetime of the calling page; clears it on unmount. */
export function useHeader({ title }: HeaderContent): void {
  const { setEntry, removeEntry } = useContext(HeaderContext);
  const id = useId();
  useEffect(() => {
    setEntry(id, { title });
  }, [id, title, setEntry]);
  useEffect(() => () => removeEntry(id), [id, removeEntry]);
}

/** The shell's read side. */
export function useHeaderContent(): HeaderContent {
  return useContext(HeaderContext).content;
}
